import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api, type User } from './api';

interface AuthValue {
  user: User | null;
  loading: boolean;
  needsSetup: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { user: me } = await api.get<{ user: User }>('/api/auth/me');
        if (!cancelled) setUser(me);
      } catch (error) {
        // A 401 is the normal signed-out path, not a failure worth surfacing.
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.error(error);
        }
        try {
          const status = await api.get<{ needs_setup: boolean }>('/api/auth/status');
          if (!cancelled) setNeedsSetup(status.needs_setup);
        } catch {
          // Server unreachable; the login screen will show the error on submit.
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: me } = await api.post<{ user: User }>('/api/auth/login', { email, password });
    setUser(me);
  }, []);

  const register = useCallback(async (email: string, password: string, fullName: string) => {
    const { user: me } = await api.post<{ user: User }>('/api/auth/register', {
      email,
      password,
      full_name: fullName,
    });
    setUser(me);
    setNeedsSetup(false);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, needsSetup, login, register, logout }),
    [user, loading, needsSetup, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
